import { useState, useRef, useEffect } from 'react';
import {
  Users,
  MapPin,
  PcCase,
  ChevronDown,
  ChevronUp,
  X,
  Search,
  Globe,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocations, Location } from '../../hooks/locations/useLocations';
import { useUnits, Unit } from '../../hooks/units/useUnits';
import { useLocationUnits } from '../../hooks/locations/useLocationUnits';
import { useContracts, Contract } from '../../hooks/contracts';

interface Filters {
  locationId: string;
  contractId: string;
  unitId: string;
  locationName?: string;
  unitName?: string;
  contractName?: string;
}

interface FilterPanelProps {
  filters: Filters;
  onChange: (filters: Filters) => void;
}

export default function FilterPanel({ filters, onChange }: FilterPanelProps) {
  const [activeFilter, setActiveFilter] = useState<string | null>('global');

  const isGlobalSelected = !filters.locationId && !filters.contractId && !filters.unitId;

  const handleFilterSelect = (filterId: string) => {
    setActiveFilter(activeFilter === filterId ? null : filterId);
  };

  const handleGlobalFilterSelect = () => {
    onChange({
      locationId: '',
      unitId: '',
      contractId: '',
    });
  };

  const handleContractSelect = (id: string, name?: string) => {
    if (id) {
      onChange({
        locationId: '',
        unitId: '',
        contractId: id,
        contractName: name || '',
        locationName: '',
        unitName: '',
      });
    } else {
      onChange({ ...filters, contractId: id, contractName: '' });
    }
  };

  const handleLocationSelect = (id: string, name?: string) => {
    if (id) {
      onChange({
        contractId: '',
        unitId: '',
        locationId: id,
        locationName: name || '',
        contractName: '',
        unitName: '',
      });
    } else {
      onChange({ ...filters, locationId: id, locationName: '' });
    }
  };

  const handleUnitSelect = (id: string, name?: string) => {
    if (id) {
      onChange({
        contractId: '',
        locationId: '',
        unitId: id,
        unitName: name || '',
        contractName: '',
        locationName: '',
      });
    } else {
      onChange({ ...filters, unitId: id, unitName: '' });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97, y: 16 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.4, scale: { type: 'spring', visualDuration: 0.4, bounce: 0.2 } }}
      className="relative overflow-hidden rounded-2xl bg-[var(--anl-surface)] shadow-md md:sticky md:top-4"
    >
      {/* accent bottom stripe — matches Team Insights analytics card pattern */}
      <div
        className="absolute bottom-0 left-0 right-0 h-1.5"
        style={{ backgroundColor: 'var(--anl-warning)' }}
      />
      <header className="flex items-center justify-between border-b border-[var(--anl-border)] bg-[var(--anl-surface-elev)] px-6 py-4">
        <h2 className="text-base font-semibold text-[var(--anl-text)]">Admin Type</h2>
      </header>
      <div className="flex flex-col gap-2 px-4 pb-6 pt-3">
        <motion.div
          className="overflow-hidden rounded-lg"
          initial={{ opacity: 0.9 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <motion.button
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150"
            style={
              isGlobalSelected
                ? { backgroundColor: 'var(--anl-warning)', color: '#ffffff' }
                : { backgroundColor: '#454b4d', color: '#cbd5e1' }
            }
            onClick={handleGlobalFilterSelect}
            whileTap={{ scale: 0.98 }}
          >
            <Globe size={16} className={isGlobalSelected ? 'text-white' : 'text-slate-400'} />
            <span>Global</span>
          </motion.button>
        </motion.div>

        <GenericFilterDropdown
          id="contract"
          title="Contract"
          icon={<Users size={18} />}
          isActive={activeFilter === 'contract'}
          onToggle={() => handleFilterSelect('contract')}
          selectedValue={filters.contractId}
          onSelect={handleContractSelect}
          filters={filters}
        />

        <GenericFilterDropdown
          id="location"
          title="Location"
          icon={<MapPin size={18} />}
          isActive={activeFilter === 'location'}
          onToggle={() => handleFilterSelect('location')}
          selectedValue={filters.locationId}
          onSelect={handleLocationSelect}
          filters={filters}
        />

        <GenericFilterDropdown
          id="unit"
          title="Unit"
          icon={<PcCase size={18} />}
          isActive={activeFilter === 'unit'}
          onToggle={() => handleFilterSelect('unit')}
          selectedValue={filters.unitId}
          onSelect={handleUnitSelect}
          filters={filters}
        />
      </div>
    </motion.div>
  );
}

interface GenericFilterDropdownProps {
  id: string;
  title: string;
  icon: React.ReactNode;
  isActive: boolean;
  onToggle: () => void;
  selectedValue: string;
  onSelect: (value: string, displayValue?: string) => void;
  filters: Filters;
}

function GenericFilterDropdown({
  id,
  title,
  icon,
  isActive,
  onToggle,
  selectedValue,
  onSelect,
  filters,
}: GenericFilterDropdownProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isActive && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    if (!isActive) {
      setSearchQuery('');
    }
  }, [isActive]);

  const isSelected = selectedValue !== '';

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  return (
    <div className="overflow-hidden rounded-lg">
      <AnimatePresence mode="wait">
        {isActive ? (
          <motion.div
            key="active"
            className="flex items-center justify-between rounded-lg px-3 py-2"
            style={{ backgroundColor: 'var(--anl-warning)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <div className="flex flex-1 items-center gap-1 px-1">
              <Search size={14} className="shrink-0 text-white/70" aria-hidden="true" />
              <input
                ref={searchInputRef}
                type="text"
                aria-label={`Search ${title.toLowerCase()}`}
                className="w-full border-none bg-transparent py-0.5 text-sm text-white placeholder-white/60 outline-none focus-visible:outline-none"
                placeholder={`Search ${title.toLowerCase()}…`}
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
              />
              {searchQuery && (
                <button
                  aria-label="Clear search"
                  className="shrink-0 text-white/70 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/50"
                  onClick={() => handleSearchChange('')}
                >
                  <X size={14} aria-hidden="true" />
                </button>
              )}
            </div>
            <button
              aria-label="Collapse filter"
              className="ml-1 shrink-0 rounded p-1 text-white/80 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/50"
              onClick={onToggle}
            >
              <ChevronUp size={16} aria-hidden="true" />
            </button>
          </motion.div>
        ) : (
          <motion.button
            key="inactive"
            className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150"
            style={
              isSelected
                ? { backgroundColor: 'var(--anl-warning)', color: '#ffffff' }
                : { backgroundColor: '#454b4d', color: '#cbd5e1' }
            }
            onClick={onToggle}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <div className="flex items-center gap-2">
              <span className={isSelected ? 'text-white' : 'text-slate-400'}>{icon}</span>
              <span>{title}</span>
              {isSelected && (
                <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-xs text-white">
                  Active
                </span>
              )}
            </div>
            <ChevronDown size={14} className={isSelected ? 'text-white/70' : 'text-slate-400'} />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isActive && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <FilterContents
              filterId={id}
              searchQuery={searchQuery}
              selectedValue={selectedValue}
              onSelect={onSelect}
              filters={filters}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface FilterContentsProps {
  filterId: string;
  searchQuery: string;
  selectedValue: string;
  onSelect: (value: string, displayValue?: string) => void;
  filters: Filters;
}

function FilterContents({
  filterId,
  searchQuery,
  selectedValue,
  onSelect,
  filters,
}: FilterContentsProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 5;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  switch (filterId) {
    case 'location':
      return (
        <DataFilterContents<Location>
          useDataHook={useLocations}
          searchQuery={searchQuery}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          selectedValue={selectedValue}
          onSelect={onSelect}
          limit={limit}
          dataKey="locations"
          idKey="locationid"
          displayKey="locationdesc"
          emptyMessage="No locations found"
        />
      );
    case 'contract': {
      return (
        <DataFilterContents<Contract>
          useDataHook={useContracts}
          searchQuery={searchQuery}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          selectedValue={selectedValue}
          onSelect={onSelect}
          limit={limit}
          dataKey="contracts"
          idKey="contractid"
          displayKey="contractname"
          emptyMessage="No contracts found"
        />
      );
    }
    case 'unit':
      if (filters.locationId) {
        type LocationUnitsParams = {
          searchQuery?: string;
          page?: number;
          limit?: number;
        };

        const useLocationSpecificUnits = (params: LocationUnitsParams) =>
          useLocationUnits({
            locationId: filters.locationId,
            searchQuery: params.searchQuery,
            page: params.page,
            limit: params.limit,
          });

        return (
          <DataFilterContents<Unit>
            useDataHook={useLocationSpecificUnits}
            searchQuery={searchQuery}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            selectedValue={selectedValue}
            onSelect={onSelect}
            limit={limit}
            dataKey="units"
            idKey="unitid"
            displayKey="unitdescription"
            emptyMessage="No units found for this location"
          />
        );
      }

      return (
        <DataFilterContents<Unit>
          useDataHook={useUnits}
          searchQuery={searchQuery}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          selectedValue={selectedValue}
          onSelect={onSelect}
          limit={limit}
          dataKey="units"
          idKey="unitid"
          displayKey="unitdescription"
          emptyMessage="No units found"
        />
      );
    default:
      return (
        <div className="p-3 text-center text-[var(--anl-text-muted)]">Filter not implemented</div>
      );
  }
}

interface DataFilterContentsProps<T> {
  useDataHook: (params: { searchQuery?: string; page?: number; limit?: number }) => {
    data?: Record<string, unknown>;
    isLoading: boolean;
  };
  searchQuery: string;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  selectedValue: string;
  onSelect: (value: string, displayValue?: string) => void;
  limit: number;
  dataKey: string;
  idKey: keyof T;
  displayKey: keyof T;
  emptyMessage: string;
}

function DataFilterContents<T>({
  useDataHook,
  searchQuery,
  currentPage,
  setCurrentPage,
  selectedValue,
  onSelect,
  limit,
  dataKey,
  idKey,
  displayKey,
  emptyMessage,
}: DataFilterContentsProps<T>) {
  const { data, isLoading } = useDataHook({
    searchQuery,
    page: currentPage,
    limit,
  });

  const items = data && data[dataKey] ? (data[dataKey] as T[]) : [];
  const totalPages = data?.totalPages ? Number(data.totalPages) : 1;

  return (
    <ItemListContainer
      items={items}
      isLoading={isLoading}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      selectedValue={selectedValue}
      onSelect={onSelect}
      totalPages={totalPages}
      idKey={idKey}
      displayKey={displayKey}
      emptyMessage={emptyMessage}
    />
  );
}

interface ItemListContainerProps<T> {
  items: T[];
  isLoading: boolean;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  selectedValue: string;
  onSelect: (value: string, displayValue?: string) => void;
  totalPages: number;
  idKey: keyof T;
  displayKey: keyof T;
  emptyMessage: string;
}

function ItemListContainer<T>({
  items,
  isLoading,
  currentPage,
  setCurrentPage,
  selectedValue,
  onSelect,
  totalPages,
  idKey,
  displayKey,
  emptyMessage,
}: ItemListContainerProps<T>) {
  return (
    <div className="flex flex-col border-t border-[var(--anl-border)] bg-[var(--anl-surface-elev)] p-2">
      <div className="max-h-56 overflow-y-auto overflow-x-hidden">
        {isLoading ? (
          <div className="py-2 text-center text-[var(--anl-text-muted)]">Loading…</div>
        ) : items.length > 0 ? (
          <div>
            {items.map(item => {
              const isItemSelected = selectedValue === String(item[idKey]);
              return (
                <div
                  key={String(item[idKey])}
                  className="cursor-pointer rounded p-2 text-sm transition-colors"
                  style={
                    isItemSelected
                      ? { backgroundColor: 'var(--anl-warning)', color: '#ffffff' }
                      : { color: 'var(--anl-text)' }
                  }
                  onMouseEnter={e => {
                    if (!isItemSelected)
                      (e.currentTarget as HTMLDivElement).style.backgroundColor =
                        'var(--anl-surface-elev)';
                  }}
                  onMouseLeave={e => {
                    if (!isItemSelected)
                      (e.currentTarget as HTMLDivElement).style.backgroundColor = '';
                  }}
                  onClick={() => {
                    const newValue = isItemSelected ? '' : String(item[idKey]);
                    const displayValue = newValue ? String(item[displayKey]) : '';
                    onSelect(newValue, displayValue);
                  }}
                >
                  <div className="flex items-center justify-between">
                    {String(item[displayKey])}
                    {isItemSelected && <div className="h-2 w-2 rounded-full bg-white"></div>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-2 text-center text-[var(--anl-text-muted)]">{emptyMessage}</div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-2 flex items-center justify-between border-t border-[var(--anl-border)] pt-2">
          <button
            className={`flex items-center gap-1 rounded px-2 py-1 text-xs ${
              currentPage > 1
                ? 'text-[var(--anl-text)] hover:bg-[var(--anl-surface-elev)]'
                : 'cursor-not-allowed text-[var(--anl-text-muted)]'
            }`}
            onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={14} />
            <span>Previous</span>
          </button>

          <span className="text-xs text-[var(--anl-text-muted)]">
            {currentPage} of {totalPages}
          </span>

          <button
            className={`flex items-center gap-1 rounded px-2 py-1 text-xs ${
              currentPage < totalPages
                ? 'text-[var(--anl-text)] hover:bg-[var(--anl-surface-elev)]'
                : 'cursor-not-allowed text-[var(--anl-text-muted)]'
            }`}
            onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <span>Next</span>
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
